import { meKeys, usersKeys } from 'api/queryKeys';
import { updateUserAreaPermissions } from 'api/user';
import { Box, Flex } from 'components';
import { Button, SwitchButton } from 'components/_form';
import {
  IOption,
  NestedSelect
} from 'components/_form/NestedSelect/NestedSelect';
import { collapseSelectedAreaPermissionIds } from 'components/_form/NestedSelect/parseNestedSelectSelectedLabels';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useQueryClient } from '@tanstack/react-query';
import { AreaPermissionsAttribute } from 'types/forms/userAreaPermissions';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IUserPatchResponse } from 'types/forms/user';
import { IUser } from 'types/user';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';

/** Select obszarów w tej zakładce = uprawnienie edycji (`kind: edit`), nie podglądu. */
const SELECT_AREA_PERMISSION_KIND = 'edit' as const;

function buildEditAreaPermissionsAttributes(
  existing: IUser['area_permissions'] | undefined,
  selectedAreaIds: string[]
): AreaPermissionsAttribute[] {
  const selected = new Set(
    selectedAreaIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
  );
  const editPerms = (Array.isArray(existing) ? existing : []).filter(
    (p) =>
      p.kind === SELECT_AREA_PERMISSION_KIND &&
      p.area_id != null &&
      !Number.isNaN(Number(p.area_id))
  );
  const attrs: AreaPermissionsAttribute[] = [];
  const editAreaIds = new Set(
    editPerms.map((p) => Number(p.area_id)).filter((id) => !Number.isNaN(id))
  );

  for (const p of editPerms) {
    const aid = Number(p.area_id);
    if (Number.isNaN(aid)) continue;
    if (selected.has(aid)) {
      attrs.push({
        id: Number(p.id),
        area_id: aid,
        kind: SELECT_AREA_PERMISSION_KIND
      });
    } else {
      attrs.push({ id: Number(p.id), _destroy: true });
    }
  }

  for (const aid of Array.from(selected)) {
    if (!editAreaIds.has(aid)) {
      attrs.push({ area_id: aid, kind: SELECT_AREA_PERMISSION_KIND });
    }
  }

  return attrs;
}

function mergeUserAfterAreaPermissionsPatch(prev: IUser | undefined, patch: IUser): IUser {
  if (!prev) return patch;
  return {
    ...prev,
    ...patch,
    profile: patch.profile ?? prev.profile,
    functions: patch.functions ?? prev.functions ?? [],
    areas: patch.areas ?? prev.areas ?? [],
    owned_areas: patch.owned_areas ?? prev.owned_areas ?? [],
    teams: patch.teams ?? prev.teams ?? [],
    area_permissions: patch.area_permissions ?? prev.area_permissions,
    guest_view_superordinate_areas:
      patch.guest_view_superordinate_areas ?? prev.guest_view_superordinate_areas,
    guest_view_peer_areas: patch.guest_view_peer_areas ?? prev.guest_view_peer_areas
  };
}

interface Props {
  user: IUser | undefined;
  /** `/profil/:id` albo `undefined` gdy własny profil z `/account/me`. */
  routeUserId: string | undefined;
  companyId: string;
  onUserUpdated: (user: IUser) => void;
}

export const AreaPermissionsForm = ({
  user,
  routeUserId,
  companyId,
  onUserUpdated
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedOptions, setSelectedOptions] = useState<IOption[]>([]);
  const [guestSuper, setGuestSuper] = useState(false);
  const [guestPeer, setGuestPeer] = useState(false);
  const [saving, setSaving] = useState(false);

  const profileUserId = routeUserId ?? user?.id ?? '';

  const { data: areasData = [] } = useAreasQuery();

  const areaOptions = useMemo(() => {
    try {
      return parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      );
    } catch (e) {
      console.error('AreaPermissionsForm: failed to build area tree', e);
      return [];
    }
  }, [areasData]);

  useEffect(() => {
    if (!user) return;
    const permissions = user.area_permissions;
    const list = Array.isArray(permissions) ? permissions : [];
    const editPerms = list.filter(
      (p) =>
        p.kind === SELECT_AREA_PERMISSION_KIND &&
        p.area_id != null &&
        !Number.isNaN(Number(p.area_id))
    );
    const selected = editPerms
      .map((p) =>
        findOptionByValueInTree(areaOptions, String(p.area_id))
      )
      .filter((o): o is IOption => !!o);
    setSelectedOptions(selected);
    setGuestSuper(!!user.guest_view_superordinate_areas);
    setGuestPeer(!!user.guest_view_peer_areas);
  }, [user, areaOptions]);

  const handleSave = useCallback(async () => {
    if (!profileUserId) {
      toast.error(t('anErrorOccurred'));
      return;
    }

    const collapsedIds = collapseSelectedAreaPermissionIds(areaOptions, selectedOptions);
    const selectedIds = collapsedIds.map((id) => String(id));

    const payload = {
      user: {
        guest_view_superordinate_areas: guestSuper,
        guest_view_peer_areas: guestPeer,
        area_permissions_attributes: buildEditAreaPermissionsAttributes(
          user?.area_permissions,
          selectedIds
        )
      }
    };

    setSaving(true);
    try {
      const res = (await updateUserAreaPermissions(
        profileUserId,
        payload
      )) as IUserPatchResponse;
      const updated = res?.data;
      if (!updated || typeof updated !== 'object') {
        toast.error(t('anErrorOccurred'));
        return;
      }
      onUserUpdated(mergeUserAfterAreaPermissionsPatch(user, updated));
      if (companyId) {
        await queryClient.invalidateQueries({
          queryKey: usersKeys.detail(companyId, profileUserId)
        });
      }
      await queryClient.invalidateQueries({ queryKey: meKeys.all() });
      toast.success(t('common.success'));
    } catch (e) {
      console.error(e);
      toast.error(t('anErrorOccurred'));
    } finally {
      setSaving(false);
    }
  }, [
    profileUserId,
    guestSuper,
    guestPeer,
    areaOptions,
    selectedOptions,
    user?.area_permissions,
    companyId,
    queryClient,
    onUserUpdated,
    t
  ]);

  return (
    <Flex flexDirection="column">
      <Box width="100%">
        <NestedSelect
          label="profile.userAreaPermissions"
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          options={areaOptions}
        />
      </Box>
      <Box my={4} pr={[0, 6]}>
        <SwitchButton
          label="profile.userHasRiparianAreasViewPermission"
          value={guestSuper}
          onChange={setGuestSuper}
        />
      </Box>
      <Box pr={[0, 6]}>
        <SwitchButton
          label="profile.userHasNeighboringAreasViewPermission"
          value={guestPeer}
          onChange={setGuestPeer}
        />
      </Box>
      <Flex mt={6} justifyContent="flex-end" width="100%">
        <Button
          variant="eucalyptus"
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {t('common.save')}
        </Button>
      </Flex>
    </Flex>
  );
};
